import { Component, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DateRangePresetPeriod } from '@vendasta/galaxy/datepicker';
import { DateRange } from '@vendasta/galaxy/utility/date-utils';
import { KeywordInfo } from '@vendasta/listing-products';
import { BehaviorSubject, Subscription } from 'rxjs';
import { SEODATA_TAB_GRID } from '../keyword-tracking/keyword-tracking.component';
import { KeywordTrackingService, SeoDataSettings } from '../keyword-tracking/keyword-tracking.service';
import { LocalSeoKeywordDialogComponent } from '../local-seo-keyword-dialog/local-seo-keyword-dialog.component';
import { LocalSeoSettingsDialogComponent } from '../local-seo-settings-dialog/local-seo-settings-dialog.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'local-seo-page-title-action',
  templateUrl: './local-seo-page-title-action.component.html',
  styleUrls: ['./local-seo-page-title-action.component.scss'],
  standalone: false,
})
export class LocalSeoPageTitleActionComponent implements OnDestroy {
  selectedDateRange: DateRange;
  keywordDialogRef: MatDialogRef<LocalSeoKeywordDialogComponent>;
  settingsDialogRef: MatDialogRef<LocalSeoSettingsDialogComponent>;
  subscriptions: Subscription[] = [];
  defaultDatePeriod = DateRangePresetPeriod.lastThirtyDays;
  private selectedTab$$: BehaviorSubject<string> = new BehaviorSubject<string>(SEODATA_TAB_GRID);
  canEditKeywords: boolean;
  listingProfileRedirectLink: string;
  protected isMobile = toSignal(
    this.breakpointObserver.observe('(max-width: 840px)').pipe(map((result) => result.matches)),
  );

  constructor(
    public readonly service: KeywordTrackingService,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly breakpointObserver: BreakpointObserver,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    this.service.canEditKeywords$.subscribe((value) => {
      this.canEditKeywords = value;
    });
    this.listingProfileRedirectLink = this.router
      .createUrlTree(['../listing-profile'], { relativeTo: this.route })
      .toString();
  }

  openKeywords(keywords: string[], limit: number, suggestedKeywords: KeywordInfo[]): void {
    if (!keywords || keywords.length === 0) {
      keywords = [];
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let mapUnits: string;
    this.service.getLocalStorage('mapUnits').subscribe((value) => {
      mapUnits = value ?? this.translate.instant('LOCAL_SEO.SELECT_LOCAL_SEARCH_RADIUS.MILES');
    });

    this.keywordDialogRef = this.dialog.open(LocalSeoKeywordDialogComponent, {
      data: {
        keywords: keywords,
        keywordLimit: limit,
        suggestedKeywords: suggestedKeywords,
        canEditKeywords: this.canEditKeywords,
        listingProfileRedirectLink: this.listingProfileRedirectLink,
      },
      width: '600px',
    });
    this.subscriptions.push(
      this.keywordDialogRef.afterClosed().subscribe((result) => {
        if (result !== undefined) {
          this.service.updateKeywords(result.originalKeywords, result.newKeywords);
        }
      }),
    );
  }

  openSettings(seoDataSettings: SeoDataSettings): void {
    let mapUnits: string;
    this.service.getLocalStorage('mapUnits').subscribe((value) => {
      mapUnits = value ?? this.translate.instant('LOCAL_SEO.SELECT_LOCAL_SEARCH_RADIUS.MILES');
    });

    this.settingsDialogRef = this.dialog.open(LocalSeoSettingsDialogComponent, {
      data: {
        mapRadius: seoDataSettings?.localSearchRadius,
        mapUnits: mapUnits,
        isFullSearchEnabled: seoDataSettings?.isFullSearchEnabled,
      },
      width: '600px',
    });
    this.subscriptions.push(
      this.settingsDialogRef.afterClosed().subscribe((result) => {
        if (result !== undefined) {
          this.service.updateSeoSettingsForDialog(result.mapRadius, result.isFullSearchEnabled);
          this.service.setLocalStorage(
            'mapUnits',
            result.mapUnits ?? this.translate.instant('LOCAL_SEO.SELECT_LOCAL_SEARCH_RADIUS.MILES'),
          );
        }
      }),
    );
  }

  onDateRangeSelect(value: DateRange): void {
    this.selectedTab$$.next(SEODATA_TAB_GRID);
    this.service.updateDateRange(value);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
